import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, User, Calendar, DollarSign, CheckCircle, Clock, Eye, EyeOff, BookOpen, Star } from 'lucide-react';

const LedgerBookPage = ({ family, children, onNavigate }) => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedChildren, setExpandedChildren] = useState({});
  const [groupByView, setGroupByView] = useState('month'); // 'month' or 'child' - keeping for potential future use
  const [showBalances, setShowBalances] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  // Load all approved deposits for the family
  useEffect(() => {
    const loadLedgerData = async () => {
      if (!children || children.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('LedgerBook: Loading deposit history for children:', children.map(c => c.id));

        // Load deposit history for each child
        const allDeposits = [];
        
        for (const child of children) {
          try {
            // Get all approved deposits for this child
            const response = await fetch(`${API_BASE_URL}/deposits/history/${child.id}?limit=100`);
            const result = await response.json();
            
            if (result.success && result.data) {
              // Add child info to each deposit
              const childDeposits = result.data.map(deposit => ({
                ...deposit,
                child_id: child.id,
                child_first_name: child.first_name,
                child_last_name: child.last_name,
                child_avatar: child.avatar,
                // Parse amounts to ensure they're numbers
                amount: parseFloat(deposit.amount || 0),
                bonus_earned: parseFloat(deposit.bonus_earned || 0),
                total_value: parseFloat(deposit.amount || 0) + parseFloat(deposit.bonus_earned || 0)
              }));
              
              allDeposits.push(...childDeposits);
            }
          } catch (childError) {
            console.error(`LedgerBook: Error loading deposits for child ${child.id}:`, childError);
          }
        }

        // Add month/year info to each deposit
        const depositsWithDates = allDeposits.map(deposit => {
          const date = new Date(deposit.admin_verified_at || deposit.created_at);
          return {
            ...deposit,
            month: date.getMonth() + 1,
            year: date.getFullYear(),
            monthName: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          };
        });

        // Sort by date (newest first)
        depositsWithDates.sort((a, b) => new Date(b.admin_verified_at || b.created_at) - new Date(a.admin_verified_at || a.created_at));

        console.log('LedgerBook: Loaded deposit data:', depositsWithDates.length, 'deposits');
        setLedgerData(depositsWithDates);
        
      } catch (error) {
        console.error('LedgerBook: Error loading ledger data:', error);
        setError('Failed to load deposit history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadLedgerData();
  }, [children]);

  // Group data by month/year, then by child within each month
  const groupedByMonthAndChild = ledgerData.reduce((acc, deposit) => {
    const monthKey = `${deposit.year}-${deposit.month.toString().padStart(2, '0')}`;
    const childKey = deposit.child_id;
    
    // Initialize month if it doesn't exist
    if (!acc[monthKey]) {
      acc[monthKey] = {
        monthName: deposit.monthName,
        year: deposit.year,
        month: deposit.month,
        totalAmount: 0,
        totalBonus: 0,
        totalValue: 0,
        count: 0,
        children: {}
      };
    }
    
    // Initialize child within month if it doesn't exist
    if (!acc[monthKey].children[childKey]) {
      acc[monthKey].children[childKey] = {
        childName: `${deposit.child_first_name} ${deposit.child_last_name}`,
        childAvatar: deposit.child_avatar,
        childId: deposit.child_id,
        deposits: [],
        totalAmount: 0,
        totalBonus: 0,
        totalValue: 0,
        count: 0
      };
    }
    
    // Add deposit to child within month
    acc[monthKey].children[childKey].deposits.push(deposit);
    acc[monthKey].children[childKey].totalAmount += deposit.amount;
    acc[monthKey].children[childKey].totalBonus += deposit.bonus_earned;
    acc[monthKey].children[childKey].totalValue += deposit.total_value;
    acc[monthKey].children[childKey].count++;
    
    // Add to month totals
    acc[monthKey].totalAmount += deposit.amount;
    acc[monthKey].totalBonus += deposit.bonus_earned;
    acc[monthKey].totalValue += deposit.total_value;
    acc[monthKey].count++;
    
    return acc;
  }, {});

  const toggleMonthExpansion = (key) => {
    setExpandedMonths(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleChildInMonthExpansion = (monthKey, childKey) => {
    setExpandedChildren(prev => ({
      ...prev,
      [`${monthKey}-${childKey}`]: !prev[`${monthKey}-${childKey}`]
    }));
  };

  const formatCurrency = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) return '$0.00';
    return showBalances ? `$${amount.toFixed(2)}` : '$•••••';
  };

  const getAvatarEmoji = (avatar) => {
    const avatars = {
      sprout: '🌱',
      leaf: '🍃', 
      tree: '🌳',
      seedling: '🌰',
      sapling: '🌿'
    };
    return avatars[avatar] || '🌱';
  };

  const getStatusBadge = (status) => {
    const badges = {
      admin_approved: { text: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      pending: { text: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      parent_approved: { text: 'Parent Approved', color: 'bg-blue-100 text-blue-800', icon: Clock }
    };
    
    const badge = badges[status] || badges.admin_approved;
    const Icon = badge.icon;
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </div>
    );
  };

  const DepositRow = ({ deposit }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-sm">
          {getAvatarEmoji(deposit.child_avatar)}
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {deposit.child_first_name} {deposit.child_last_name}
          </div>
          <div className="text-sm text-gray-500 flex items-center space-x-2">
            <span>{deposit.description}</span>
            <span>•</span>
            <span>{new Date(deposit.admin_verified_at || deposit.created_at).toLocaleDateString()}</span>
            {deposit.status && getStatusBadge(deposit.status)}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-bold text-gray-900">
          {formatCurrency(deposit.amount)}
        </div>
        {deposit.bonus_earned > 0 && (
          <div className="text-sm text-green-600 flex items-center">
            <Star className="w-3 h-3 mr-1" />
            +{formatCurrency(deposit.bonus_earned)} bonus
          </div>
        )}
        <div className="text-xs text-gray-400">
          Total: {formatCurrency(deposit.total_value)}
        </div>
      </div>
    </div>
  );

  const MonthSection = ({ monthKey, monthData }) => {
    const isExpanded = expandedMonths[monthKey];
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleMonthExpansion(monthKey)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-500" />
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">{monthData.monthName}</h3>
              <p className="text-sm text-gray-500">
                {monthData.count} deposit{monthData.count !== 1 ? 's' : ''} • {Object.keys(monthData.children).length} child{Object.keys(monthData.children).length !== 1 ? 'ren' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="font-bold text-gray-900">
                {formatCurrency(monthData.totalValue)}
              </div>
              <div className="text-sm text-gray-500">
                {formatCurrency(monthData.totalAmount)} + {formatCurrency(monthData.totalBonus)} bonus
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>
        
        {isExpanded && (
          <div className="border-t border-gray-100 p-4">
            <div className="space-y-3">
              {Object.entries(monthData.children)
                .sort(([, a], [, b]) => a.childName.localeCompare(b.childName))
                .map(([childKey, childData]) => (
                  <ChildInMonthSection 
                    key={childKey} 
                    monthKey={monthKey} 
                    childKey={childKey} 
                    childData={childData} 
                  />
                ))
              }
            </div>
          </div>
        )}
      </div>
    );
  };

  const ChildInMonthSection = ({ monthKey, childKey, childData }) => {
    const isExpanded = expandedChildren[`${monthKey}-${childKey}`];
    
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden ml-6 mb-3">
        <button
          onClick={() => toggleChildInMonthExpansion(monthKey, childKey)}
          className="w-full p-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-sm">
              {getAvatarEmoji(childData.childAvatar)}
            </div>
            <div className="text-left">
              <h4 className="font-medium text-gray-900">{childData.childName}</h4>
              <p className="text-sm text-gray-500">
                {childData.count} deposit{childData.count !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="font-semibold text-gray-900">
                {formatCurrency(childData.totalValue)}
              </div>
              <div className="text-xs text-gray-500">
                {formatCurrency(childData.totalAmount)} + {formatCurrency(childData.totalBonus)} bonus
              </div>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </button>
        
        {isExpanded && (
          <div className="border-t border-gray-200 p-3">
            <div className="space-y-2">
              {childData.deposits
                .sort((a, b) => new Date(b.admin_verified_at || b.created_at) - new Date(a.admin_verified_at || a.created_at))
                .map((deposit) => (
                <div key={`${deposit.id}-${deposit.child_id}`} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{deposit.description}</div>
                    <div className="text-xs text-gray-500 flex items-center space-x-2">
                      <span>{new Date(deposit.admin_verified_at || deposit.created_at).toLocaleDateString()}</span>
                      {deposit.status && getStatusBadge(deposit.status)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(deposit.amount)}
                    </div>
                    {deposit.bonus_earned > 0 && (
                      <div className="text-xs text-green-600 flex items-center">
                        <Star className="w-2 h-2 mr-1" />
                        +{formatCurrency(deposit.bonus_earned)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading family ledger...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Ledger</div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => onNavigate && onNavigate('familyDashboard')}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </button>
              <div className="flex items-center space-x-3">
                <BookOpen className="w-8 h-8 text-green-600" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Family Ledger Book</h1>
                  <p className="text-gray-600">{family?.name || 'Your Family'} - Transaction History</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowBalances(!showBalances)}
              className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showBalances ? (
                <Eye className="w-4 h-4 text-gray-600" />
              ) : (
                <EyeOff className="w-4 h-4 text-gray-600" />
              )}
              <span className="text-sm font-medium text-gray-700">
                {showBalances ? 'Hide' : 'Show'} Amounts
              </span>
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">Total Deposits</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(ledgerData.reduce((sum, d) => sum + d.amount, 0))}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3 mb-2">
              <Star className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-gray-900">Total Bonuses</h3>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {formatCurrency(ledgerData.reduce((sum, d) => sum + d.bonus_earned, 0))}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3 mb-2">
              <User className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Total Transactions</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{ledgerData.length}</p>
          </div>
        </div>

        {/* Ledger Content */}
        <div className="space-y-4">
          {Object.keys(groupedByMonthAndChild).length > 0 &&
            Object.entries(groupedByMonthAndChild)
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([monthKey, monthData]) => (
                <MonthSection key={monthKey} monthKey={monthKey} monthData={monthData} />
              ))
          }
        </div>

        {ledgerData.length === 0 && !loading && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No deposits yet</h3>
            <p className="text-gray-600">
              {children?.length === 0 
                ? "Add children to your family to start tracking deposits." 
                : "Start approving deposit requests to see them in your ledger book."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LedgerBookPage;